// Lara AI side panel for a quote: analysis (left) + shared chat (right). Solid, follows light/dark.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Sparkles, X, Loader2, RefreshCw, MessageCircle, FileSearch, Cpu } from "lucide-react";
import api from "../../lib/api";
import { ThemeToggle, useTheme } from "../../context/ThemeContext";
import LaraAnalysis from "./LaraAnalysis";
import LaraChat from "./LaraChat";

export default function LaraPanel({ quoteId, quoteRef, onClose }) {
  const lightOn = useTheme().theme === "light";
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("analysis");
  const [mode, setModeState] = useState(() => localStorage.getItem("lara_mode") || "full");
  const setMode = (m) => { setModeState(m); localStorage.setItem("lara_mode", m); };

  useEffect(() => {
    api.get(`/lara/quote/${quoteId}`).then(({ data }) => setState(data)).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load Lara"));
  }, [quoteId]);

  const analyse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lara/quote/${quoteId}/analyse`, {}, { timeout: 190000 });
      setState((s) => ({ ...s, analysis: data }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Lara couldn't analyse this quote"); }
    finally { setBusy(false); }
  };

  const a = state?.analysis;
  const bg = lightOn ? "#FFFFFF" : "#0F1420";
  return createPortal(
    <div className={`fixed inset-0 z-[210] flex justify-end ${lightOn ? "bg-slate-900/45" : "bg-black/70"}`} onClick={onClose}
      data-testid="lara-panel" data-talmia-theme={lightOn ? "light" : undefined}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: bg }}
        className={`h-full w-full max-w-[1180px] flex flex-col border-l ${lightOn ? "!border-slate-200" : "border-white/10"} shadow-2xl`}>
        <div className="px-4 sm:px-5 py-3 border-b border-white/[0.06] flex items-center gap-3 flex-wrap">
          <span className="w-9 h-9 rounded-xl grid place-items-center bg-gradient-to-br from-violet-500 to-amber-500 text-white shrink-0"><Sparkles className="w-4.5 h-4.5" /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold text-white"><span className="sm:hidden">Lara</span><span className="hidden sm:inline">Lara · Construction sales coach</span></div>
            <div className="text-[11px] text-slate-500 font-mono truncate">{quoteRef}<span className="hidden sm:inline"> · sales coach</span>{state?.model && <> · <Cpu className="w-3 h-3 inline" /> {state.model.model}</>}</div>
          </div>
          <button type="button" onClick={analyse} disabled={busy || !state?.eligibility?.eligible} data-testid="lara-analyse"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-[13px] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : a ? <RefreshCw className="w-4 h-4" /> : <FileSearch className="w-4 h-4" />}
            <span className="hidden sm:inline">{busy ? "Lara is reading the quote…" : a ? "Re-analyse" : "Analyse"}</span>
            <span className="sm:hidden">{busy ? "Reading…" : a ? "Redo" : "Analyse"}</span>
          </button>
          <ThemeToggle className="!w-9 !h-9" />
          <button type="button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5" data-testid="lara-close" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="lg:hidden flex gap-1.5 px-4 pt-2">
          {[["analysis", "Analysis", FileSearch], ["chat", "Chat", MessageCircle]].map(([k, l, I]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`lara-tab-${k}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border ${tab === k ? "border-violet-400/60 bg-violet-500/15 text-violet-200" : "border-white/10 text-slate-400"}`}><I className="w-3.5 h-3.5" />{l}</button>
          ))}
        </div>
        {!state ? (
          <div className="flex-1 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : !state.eligibility?.eligible ? (
          <div className="flex-1 grid place-items-center p-6 text-center text-slate-400 text-sm" data-testid="lara-not-eligible">{state.eligibility?.reason}</div>
        ) : (
          <div className="flex-1 min-h-0 flex">
            <div className={`${tab === "analysis" ? "flex" : "hidden"} lg:flex flex-col flex-1 min-w-0 overflow-y-auto p-4 sm:p-5`}>
              {a ? (
                <>
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <div className="text-[11px] text-slate-500 font-mono" data-testid="lara-analysis-meta">
                      Analysed {new Date(a.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} by {a.by} · {a.model}
                      {a.lines != null && ` · ${a.lines} lines · £${Number(a.quote_total || 0).toLocaleString("en-GB")}`}
                    </div>
                    <div className="inline-flex rounded-lg border border-white/10 p-0.5" data-testid="lara-mode-toggle">
                      {[["basic", "Basic"], ["full", "Full"]].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setMode(k)} data-testid={`lara-mode-${k}`}
                          title={k === "basic" ? "No-nonsense version: what to ask, say and offer" : "Everything Lara found"}
                          className={`px-3 py-1 rounded-md text-[12px] font-bold ${mode === k ? "bg-violet-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  <LaraAnalysis a={a} mode={mode} />
                </>
              ) : (
                <div className="m-auto max-w-sm text-center space-y-3" data-testid="lara-empty">
                  <Sparkles className="w-8 h-8 text-violet-300 mx-auto" />
                  <div className="text-white font-semibold">Let Lara read this quote</div>
                  <div className="text-[13px] text-slate-400">She'll break down the build, every product and how it's fitted, upsells, building regs, the questions to ask and a call plan, using our Hub articles. Takes about 20–40 seconds.</div>
                </div>
              )}
            </div>
            <div className={`${tab === "chat" ? "flex" : "hidden"} lg:flex flex-col w-full lg:w-[400px] shrink-0 lg:border-l border-white/[0.06] min-h-0`}>
              <LaraChat quoteId={quoteId} initial={state.chat || []} />
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
